import UserJson from './shared/UserJson';
import { ValidationErrorType, ValidationResult, ValidationErrorMap } from './shared/Validation';
import fetch, { Headers, Response, RequestInfo, RequestInit } from 'node-fetch';
import { BirthdayBookingStatus } from './shared/BirthdayBookingStatus';
import BirthdayBookingJson, { BirthdayBookingStaffAssignmentJson, BirthdayBookingLocationAssignmentJson, BirthdayBookingCustomerViewJson, BirthdayBookingProgramChangeJson, BirthdayBookingWithOverbookingInfoJson } from './shared/BirthdayBookingJson';
import BirthdayBookingInfoJson from './shared/BirthdayBookingInfoJson';
import SettingsJson, { SettingsPublicJson } from './shared/SettingsJson';
import SettingsNotificationsJson from './shared/SettingsNotificationsJson';
import CategoryTypeJson from './shared/CategoryTypeJson';
import BirthdayStaffAvailableJson from './shared/BirthdayStaffAvailableJson';
import BirthdayProgramJson from './shared/BirthdayProgramJson';
import PaymentExportInfoJson from './shared/PaymentExportInfoJson';
import DateFormatter from './shared/DateFormatter';
import HolidayProgramJson, { HolidayProgramInfoJson, HolidayProgramWithBookingJson, HolidayProgramWithBookingsJson } from './shared/HolidayProgramJson';
import { HolidayProgramBookingStatus } from './shared/HolidayProgramBookingStatus';
import { Gender } from './shared/Gender';
import HolidayProgramBookingJson from './shared/HolidayProgramBookingJson';
import { PaymentExportType } from './shared/PaymentExportType';
import WorkshopJson, { WorkshopInfoJson, WorkshopWithBookingJson, WorkshopWithBookingsJson } from './shared/WorkshopJson';
import WorkshopBookingJson from './shared/WorkshopBookingJson';
import GroupBookingJson, { GroupBookingCustomerViewJson } from './shared/GroupBookingJson';
import GroupBookingProgramJson from './shared/GroupBookingProgramJson';
import { GroupBookingStatus } from './shared/GroupBookingStatus';
import { WorkshopBookingStatus } from './shared/WorkshopBookingStatus';
import MemberJson from './shared/MemberJson';
import { MemberStatus } from './shared/MemberStatus';
import MembershipTermJson from './shared/MembershipTermJson';
import GroupBookingInfoJson from './shared/GroupBookingInfoJson';
import ModulesJson from './shared/ModulesJson';
import { MembershipType } from './shared/MembershipType';
import MailingRecipientJson from './shared/MailingRecipientJson';
import { ClientMailingCustomerInfoJson, ClientMailingJson } from './shared/ClientJson';
import { MailingRecipientStatus } from './shared/MailingRecipientStatus';
import MailingListJson from './shared/MailingListJson';
import RentalBookingInfoJson from './shared/RentalBookingInfoJson';
import RentalItemJson, { RentalItemInfoJson } from './shared/RentalItemJson';
import RentalAccountJson, { RentalAccountInfoJson } from './shared/RentalAccountJson';
import RentalBookingJson, { RentalBookingCustomerViewJson } from './shared/RentalBookingJson';
import RentalConditionJson from './shared/RentalConditionJson'
import { RentalBookingStatus } from './shared/RentalBookingStatus';
import MailingMessageJson from './shared/MailingMessageJson';
import MailingMessageRecipientInfoJson from './shared/MailingMessageRecipientInfoJson';
import MailingMessageAttachmentJson from './shared/MailingMessageAttachmentJson';
import ClientJson from './shared/ClientJson';
import EventJson, { EventWithBookingJson, EventWithBookingsJson, EventsInfoJson } from './shared/EventJson';
import { EventStatus } from './shared/EventStatus';
import EventBookingJson from './shared/EventBookingJson';
import { EventBookingStatus } from './shared/EventBookingStatus';
import EventTemplateJson from './shared/EventTemplateJson';
import { EventTemplateStatus } from './shared/EventTemplateStatus';
import EventAppointmentJson from './shared/EventAppointmentJson';
import LocationJson from './shared/LocationJson';
import MaterialItemJson from './shared/MaterialItemJson';
import { CategoryTypeType } from './shared/CategoryTypeType';
import PriceTemplateJson from './shared/PriceTemplateJson';
import RentalTimeSlotJson from './shared/RentalTimeSlotJson';
import { WeekDay } from './shared/WeekDay';

export abstract class RestError extends Error {

}

export class ServerError extends RestError {
   constructor(errorCode: string) {
      super();
      this.name = "ServerError";
      this.message = "Ein Serverfehler ist aufgetreten. Bitte kontaktieren Sie den Support mit dem Fehlercode '" + errorCode + "'.";
   }
}

export class NotFoundError extends RestError {
   constructor() {
      super();
      this.name = "NotFoundError";
      this.message = "Das angeforderte Objekt existiert nicht.";
   }
}

export class ConnectionError extends RestError {
   constructor() {
      super();
      this.name = "ConnectionError";
      this.message = "Ein Verbindungsfehler ist aufgetreten.";
   }
}

export class AuthorizationError extends RestError {
   constructor() {
      super();
      this.name = "AuthorizationError";
      this.message = "Die verwendeten Zugangsdaten sind ungültig.";
   }
}

export class ValidationError<T> extends RestError {
   readonly validationResult: ValidationResult<T>;

   constructor(validationResult: ValidationResult<T>) {
      super();
      this.name = "ValidationError";
      this.message = "Die eingegebenen Daten sind ungültig.\n\n" + JSON.stringify(validationResult);
      this.validationResult = validationResult;
   }
}

export default class RestClient {
   authHeader: string[][] = [];

   static serverApiUrlBase = process?.env?.VUE_APP_API_ENDPOINT ?? "";

   private static b64EncodeUnicode(str: string): string {
      // https://stackoverflow.com/questions/30106476/using-javascripts-atob-to-decode-base64-doesnt-properly-decode-utf-8-strings
      // first we use encodeURIComponent to get percent-encoded UTF-8,
      // then we convert the percent encodings into raw bytes which
      // can be fed into btoa.
      return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g,
         function toSolidBytes(match, p1) {
            return String.fromCharCode(parseInt(p1, 16));
         }));
   }
   private static buildAuthHeader(username: string, password: string): string[][] {
      return [['Authorization', 'Basic ' + RestClient.b64EncodeUnicode(username + ':' + password)]];
   }
   constructor(username: string, password: string) {
      this.authHeader = RestClient.buildAuthHeader(username, password);
   }

   private static async request<T>(url: RequestInfo, init?: RequestInit): Promise<Response> {
      var result: Response;
      //console.log(init?.method || "get", url)
      try {
         result = await fetch(url, init);
      } catch (e) {
         console.log("ConnectionError", e);
         throw new ConnectionError();
      }

      if (!result.ok) {
         switch (result.status) {
            case 422:
               let errorJson = await result.json();
               throw new ValidationError(new ValidationResult(errorJson as ValidationErrorMap<T>));
            case 401:
               throw new AuthorizationError();
            case 404:
               throw new NotFoundError();
            case 500:
               throw new ServerError(await result.text());
            default:
               throw new ServerError("Response Status " + result.status);
         }
      }

      return result;
   }

   async getUsers(): Promise<UserJson[]> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "user", {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as UserJson[];
   }

   async getUser(id: number): Promise<UserJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "user/" + id, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as UserJson;
   }

   async updateUser(user: UserJson): Promise<UserJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "user/" + user.ID, {
         method: "put",
         headers: new Headers(this.authHeader.concat([['Content-Type', 'application/json']])),
         body: JSON.stringify(user),
      });

      return (await result.json()) as UserJson;
   }

   async insertUser(user: UserJson): Promise<UserJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "user", {
         method: "post",
         headers: new Headers(this.authHeader.concat([['Content-Type', 'application/json']])),
         body: JSON.stringify(user)
      });

      return (await result.json()) as UserJson;
   }

   async deleteUser(id: number): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "user/" + id, {
         method: "delete",
         headers: new Headers(this.authHeader)
      });
   }

   async changePassword(id: number, password: string): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "user/" + id + "/password", {
         method: "put",
         headers: new Headers(this.authHeader),
         body: password
      });
   }

   async getBirthdayBookings(
      states: BirthdayBookingStatus[] | null = null,
      staffId: number | null = null,
      afterDateTime: Date | null = null,
      beforeDateTime: Date | null = null,
      searchString: string | null = null,
      order: "ASC" | "DESC" | null = "ASC",
      skipAmount: number | null = null): Promise<{ birthdayBookings: BirthdayBookingJson[], moreAvailable: boolean; }> {

      let requestParams = [];

      if (states) requestParams.push("states=" + states.join(","));
      if (staffId) requestParams.push("staffId=" + staffId);
      if (afterDateTime) requestParams.push("afterDateTime=" + DateFormatter.formatISODateTime(afterDateTime));
      if (beforeDateTime) requestParams.push("beforeDateTime=" + DateFormatter.formatISODateTime(beforeDateTime));
      if (searchString) requestParams.push("search=" + encodeURIComponent(searchString));
      if (order) requestParams.push("order=" + order);
      if (skipAmount) requestParams.push("skip=" + skipAmount);

      let requestParam = requestParams.length > 0 ? "?" + requestParams.join("&") : "";

      let result = await RestClient.request(RestClient.serverApiUrlBase + "birthday" + requestParam, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as { birthdayBookings: BirthdayBookingJson[], moreAvailable: boolean; };
   }

   async getBirthdayBooking(id: number): Promise<BirthdayBookingWithOverbookingInfoJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "birthday/" + id, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as BirthdayBookingWithOverbookingInfoJson;
   }

   static async requestBirthday(booking: BirthdayBookingJson): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "birthday/request", {
         method: "post",
         headers: new Headers([['Content-Type', 'application/json']]),
         body: JSON.stringify(booking)
      });
   }

   static async getBirthdayBookingInfo(): Promise<BirthdayBookingInfoJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "birthday/info", {
         method: "get"
      });

      return (await result.json()) as BirthdayBookingInfoJson;
   }

   async getBirthdayBookingStatistics(
      afterDateTime: Date | null = null,
      beforeDateTime: Date | null = null,): Promise<{ programTitle: string, count: string; }[]> {

      let requestParams = [];

      if (afterDateTime) requestParams.push("afterDateTime=" + DateFormatter.formatISODateTime(afterDateTime));
      if (beforeDateTime) requestParams.push("beforeDateTime=" + DateFormatter.formatISODateTime(beforeDateTime));

      let requestParam = requestParams.length > 0 ? "?" + requestParams.join("&") : "";

      let result = await RestClient.request(RestClient.serverApiUrlBase + "birthday/stats" + requestParam, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as { programTitle: string, count: string; }[];
   }


   async getBirthdayBookingEventLocationAvailability(beginDateTime: Date, endDateTime: Date, excludeBirthdayBookingId: number | null = null): Promise<{ [index: string]: boolean; }> {
      var params = "?beginDateTime=" + beginDateTime.toISOString() + "&endDateTime=" + endDateTime.toISOString();
      if (excludeBirthdayBookingId) {
         params += "&excludeBirthdayBookingId=" + excludeBirthdayBookingId;
      }

      let result = await RestClient.request(RestClient.serverApiUrlBase + "birthday/locationAvailability" + params, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json());
   }

   static async getBirthdayBookingDateSlots(date: Date, restrictStaffIds: number[] | null): Promise<{ beginDateTime: string, endDateTime: string; }[]> {
      var params = "?date=" + DateFormatter.formatISODate(date);
      if (restrictStaffIds) {
         params += "&staffIds=" + restrictStaffIds.join(",");
      }
      let result = await RestClient.request(RestClient.serverApiUrlBase + "birthday/slots" + params, {
         method: "get"
      });

      return (await result.json()) as { beginDateTime: string, endDateTime: string; }[];
   }

   async getBirthdaySimultanousCount(beginDateTime: Date, endDateTime: Date, excludeBirthdayBookingId: number | null = null): Promise<{ countSimultanous: number, maxCountSimultanous: number; }> {
      var params = "?beginDateTime=" + beginDateTime.toISOString() + "&endDateTime=" + endDateTime.toISOString();
      if (excludeBirthdayBookingId) {
         params += "&excludeBirthdayBookingId=" + excludeBirthdayBookingId;
      }

      let result = await RestClient.request(RestClient.serverApiUrlBase + "birthday/count" + params, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json());
   }

   async getBirthdaySimultaneousProgramCount(beginDateTime: Date, endDateTime: Date, programCategory: string, programTitle: string, excludeBirthdayBookingId: number | null = null): Promise<{ simultaneousProgramCount: number, maxSimultaneousProgramCount: number | null; }> {
      var params = "?beginDateTime=" + beginDateTime.toISOString() + "&endDateTime=" + endDateTime.toISOString() + "&programCategory=" + programCategory + "&programTitle=" + programTitle;
      if (excludeBirthdayBookingId) {
         params += "&excludeBirthdayBookingId=" + excludeBirthdayBookingId;
      }

      let result = await RestClient.request(RestClient.serverApiUrlBase + "birthday/programCount" + params, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json());
   }

   static async getBirthdayCapacity(beginDateTime: Date, endDateTime: Date, programCategory: string, programTitle: string): Promise<{ isTotalCapacityReached: boolean, isProgramCapacityReached: boolean; }> {
      var params = "?beginDateTime=" + beginDateTime.toISOString() + "&endDateTime=" + endDateTime.toISOString() + "&programCategory=" + programCategory + "&programTitle=" + programTitle;

      let result = await RestClient.request(RestClient.serverApiUrlBase + "birthday/capacity" + params, {
         method: "get"
      });

      return (await result.json());
   }

   async getBirthdayPrograms(): Promise<BirthdayProgramJson[]> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "birthday/program", {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as BirthdayProgramJson[];
   }

   async getBirthdayProgram(id: number): Promise<BirthdayProgramJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "birthday/program/" + id, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as BirthdayProgramJson;
   }

   async addBirthdayProgram(program: BirthdayProgramJson): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "birthday/program", {
         method: "post",
         body: JSON.stringify(program),
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
      });
   }

   async updateBirthdayProgram(birthdayProgram: BirthdayProgramJson): Promise<BirthdayProgramJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "birthday/program/" + birthdayProgram.ID, {
         method: "put",
         body: JSON.stringify(birthdayProgram),
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
      });

      return (await result.json()) as BirthdayProgramJson;
   }

   async deleteBirthdayProgram(program: BirthdayProgramJson): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "birthday/program/" + program.ID, {
         method: "delete",
         headers: new Headers(this.authHeader)
      });
   }

   async deleteBirthday(birthday: BirthdayBookingJson): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "birthday/" + birthday.ID, {
         method: "delete",
         headers: new Headers(this.authHeader)
      });
   }

   async updateBirthday(birthday: BirthdayBookingJson): Promise<BirthdayBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "birthday/" + birthday.ID, {
         method: "put",
         body: JSON.stringify(birthday),
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
      });

      return (await result.json()) as BirthdayBookingJson;
   }

   async cancelBirthday(birthdayId: number): Promise<BirthdayBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "birthday/" + birthdayId + "/cancel", {
         method: "put",
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
      });

      return (await result.json()) as BirthdayBookingJson;
   }

   async addBirthday(birthday: BirthdayBookingJson): Promise<BirthdayBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "birthday/", {
         method: "post",
         body: JSON.stringify(birthday),
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
      });

      return (await result.json()) as BirthdayBookingJson;
   }

   async assignBirthdayStaff(birthday: BirthdayBookingStaffAssignmentJson): Promise<BirthdayBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "birthday/" + birthday.ID + "/assignStaff", {
         method: "put",
         body: JSON.stringify(birthday),
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
      });

      return (await result.json()) as BirthdayBookingJson;
   }

   async sendBirthdayNoStaffMessageToCustomer(id: number): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "birthday/" + id + "/noStaffAvailable", {
         method: "put",
         headers: new Headers(this.authHeader),
      });
   }

   async sendBirthdayOfferToCustomer(birthday: BirthdayBookingLocationAssignmentJson): Promise<BirthdayBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "birthday/" + birthday.ID + "/sendBirthdayOfferToCustomer", {
         method: "put",
         body: JSON.stringify(birthday),
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
      });

      return (await result.json()) as BirthdayBookingJson;
   }

   static async getBirthdayBookingUsingToken(id: number, token: string): Promise<BirthdayBookingCustomerViewJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "birthday/" + id + "/token", {
         method: "get",
         headers: new Headers([['Authorization', 'Bearer ' + token]])
      });

      return (await result.json()) as BirthdayBookingCustomerViewJson;
   }

   static async bookBirthdayUsingToken(id: number, token: string): Promise<BirthdayBookingCustomerViewJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "birthday/" + id + "/token/book", {
         method: "put",
         headers: new Headers([['Authorization', 'Bearer ' + token]])
      });

      return (await result.json()) as BirthdayBookingCustomerViewJson;
   }

   // deprecated
   /* static async changeBirthdayProgramUsingToken(programChange: BirthdayBookingProgramChangeJson, token: string): Promise<BirthdayBookingCustomerViewJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "birthday/" + programChange.ID + "/token/changeProgram", {
         method: "put",
         body: JSON.stringify(programChange),
         headers: new Headers([['Content-Type', 'application/json'], ['Authorization', 'Bearer ' + token]])
      });

      return (await result.json()) as BirthdayBookingCustomerViewJson;
   } */

   static async cancelBirthdayUsingToken(id: number, token: string): Promise<BirthdayBookingCustomerViewJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "birthday/" + id + "/token/cancel", {
         method: "put",
         headers: new Headers([['Authorization', 'Bearer ' + token]])
      });

      return (await result.json()) as BirthdayBookingCustomerViewJson;
   }

   async getBirthdayStaffAvailability(staffId: number | null = null): Promise<BirthdayStaffAvailableJson[]> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "birthday/staff" + (staffId ? ("/" + staffId) : ""), {
         method: "get",
         headers: new Headers([...this.authHeader]),
      });

      return (await result.json()) as BirthdayStaffAvailableJson[];
   }

   async deleteBirthdayStaffAvailability(staffAvailability: BirthdayStaffAvailableJson): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "birthday/staff/" + staffAvailability.staffId + "/" + staffAvailability.ID, {
         method: "delete",
         headers: new Headers([...this.authHeader])
      });
   }

   async addBirthdayStaffAvailability(staffAvailability: BirthdayStaffAvailableJson): Promise<BirthdayStaffAvailableJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "birthday/staff/" + staffAvailability.staffId, {
         method: "post",
         body: JSON.stringify(staffAvailability),
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
      });

      return (await result.json()) as BirthdayStaffAvailableJson;
   }

   async getSettings(): Promise<SettingsJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "settings", {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as SettingsJson;
   }

   static async getPublicSettings(): Promise<SettingsPublicJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "settings/public", {
         method: "get",
      });

      return (await result.json()) as SettingsPublicJson;
   }

   static async checkModulesActive(modules: string[]): Promise<{[index: string]: boolean} | null> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "settings/activeModule?modules=" + modules.join(","), {
         method: "get",
      });

      return (await result.json()) as {[index: string]: boolean} | null;
   }

   async saveSettings(settings: SettingsJson): Promise<SettingsJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "settings", {
         method: "post",
         body: JSON.stringify(settings),
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
      });

      return (await result.json()) as SettingsJson;
   }

   async getAllModules(): Promise<ModulesJson[]> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "settings/modules", {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as ModulesJson[];
   }

   async getSettingsNotifications(): Promise<SettingsNotificationsJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "settings/notifications", {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as SettingsNotificationsJson;
   }

   async saveSettingsNotifications(notifications: SettingsNotificationsJson): Promise<SettingsNotificationsJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "settings/notifications", {
         method: "post",
         body: JSON.stringify(notifications),
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
      });

      return (await result.json()) as SettingsNotificationsJson;
   }

   async getCategoryTypes(types: CategoryTypeType[] | null = null): Promise<CategoryTypeJson[]> {
      let requestParams = [];

      if (types) requestParams.push("types=" + types.join(","));

      let requestParam = requestParams.length > 0 ? "?" + requestParams.join("&") : "";

      let result = await RestClient.request(RestClient.serverApiUrlBase + "settings/categoryTypes" + requestParam, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as CategoryTypeJson[];
   }

   async getCategoryType(categoryTypeId: number): Promise<CategoryTypeJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "settings/categoryType/" + categoryTypeId, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as CategoryTypeJson;
   }

   async addCategoryType(categoryTypeJson: CategoryTypeJson): Promise<CategoryTypeJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "settings/categoryType", {
         method: "post",
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
         body: JSON.stringify(categoryTypeJson)
      });

      return (await result.json()) as CategoryTypeJson;
   }

   async updateCategoryType(categoryTypeJson: CategoryTypeJson): Promise<CategoryTypeJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "settings/categoryType/" + categoryTypeJson.ID, {
         method: "put",
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
         body: JSON.stringify(categoryTypeJson)
      });

      return (await result.json()) as CategoryTypeJson;
   }

   async deleteCategoryType(categoryTypeId: number): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "settings/categoryType/" + categoryTypeId, {
         method: "delete",
         headers: new Headers(this.authHeader)
      });
   }

   async getCategoryTypeTitleAvailability(title: string, types: CategoryTypeType[] | null = null): Promise<boolean> {
      let requestParams = [];

      requestParams.push("title=" + title);
      if (types) requestParams.push("types=" + types.join(","));

      let requestParam = requestParams.length > 0 ? "?" + requestParams.join("&") : "";

      let result = await RestClient.request(RestClient.serverApiUrlBase + "settings/categoryTypeAvailability" + requestParam, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as boolean;
   }

   /* async getSettingsBirthdayPrograms(): Promise<{ [index: string]: BirthdayProgramJson[]; }> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "settings/birthdayPrograms", {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as { [index: string]: BirthdayProgramJson[]; };
   }

   async saveSettingsBirthdayPrograms(birthdayPrograms: { [index: string]: BirthdayProgramJson[]; }): Promise<{ [index: string]: BirthdayProgramJson[]; }> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "settings/birthdayPrograms", {
         method: "post",
         body: JSON.stringify(birthdayPrograms),
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
      });

      return (await result.json()) as { [index: string]: BirthdayProgramJson[]; };
   } */

   async getBirthdayBookingsReadyForPaymentExport(): Promise<BirthdayBookingJson[]> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "payment/birthday", {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as BirthdayBookingJson[];
   }

   async exportPaymentsForBirthdayBookings(): Promise<PaymentExportInfoJson | null> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "payment/birthday/export", {
         method: "post",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as PaymentExportInfoJson | null;
   }

   async getHolidayProgramBookingsReadyForPaymentExport(): Promise<HolidayProgramWithBookingJson[]> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "payment/holidayProgram/booking", {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as HolidayProgramWithBookingJson[];
   }

   async exportPaymentsForHolidayProgramBookings(): Promise<PaymentExportInfoJson | null> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "payment/holidayProgram/booking/export", {
         method: "post",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as PaymentExportInfoJson | null;
   }

   async getWorkshopBookingsReadyForPaymentExport(): Promise<WorkshopWithBookingJson[]> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "payment/workshop/booking", {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as WorkshopWithBookingJson[];
   }

   async exportPaymentsForWorkshopBookings(): Promise<PaymentExportInfoJson | null> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "payment/workshop/booking/export", {
         method: "post",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as PaymentExportInfoJson | null;
   }

   async getGroupBookingsReadyForPaymentExport(): Promise<GroupBookingJson[]> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "payment/groupBooking", {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as GroupBookingJson[];
   }

   async exportPaymentsForGroupBookings(): Promise<PaymentExportInfoJson | null> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "payment/groupBooking/export", {
         method: "post",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as PaymentExportInfoJson | null;
   }

   async getEventBookingsReadyForPaymentExport(): Promise<EventWithBookingJson[]> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "payment/event/booking", {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as EventWithBookingJson[];
   }

   async exportPaymentsForEventBookings(): Promise<PaymentExportInfoJson | null> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "payment/event/booking/export", {
         method: "post",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as PaymentExportInfoJson | null;
   }


   async getRecentPaymentExports(type: PaymentExportType | null = null): Promise<PaymentExportInfoJson[]> {
      let requestParam = type ? "?type=" + type : "";

      let result = await RestClient.request(RestClient.serverApiUrlBase + "payment/export" + requestParam, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as PaymentExportInfoJson[];
   }

   async downloadPaymentExportFile(info: PaymentExportInfoJson): Promise<ArrayBuffer> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "payment/export/" + info.ID, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return await result.arrayBuffer();
   }

   async getHolidayPrograms(): Promise<HolidayProgramJson[]> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "holidayProgram", {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as HolidayProgramJson[];
   }

   static async getHolidayProgramInfo(): Promise<HolidayProgramInfoJson[]> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "holidayProgram/info", {
         method: "get"
      });

      return (await result.json()) as HolidayProgramInfoJson[];
   }

   static async requestHolidayProgramBooking(programId: number, booking: HolidayProgramBookingJson): Promise<HolidayProgramBookingStatus.waitingList | HolidayProgramBookingStatus.pending> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "holidayProgram/" + programId + "/register", {
         method: "post",
         body: JSON.stringify(booking),
         headers: new Headers([['Content-Type', 'application/json']]),
      });

      return (await result.text()) as HolidayProgramBookingStatus.waitingList | HolidayProgramBookingStatus.pending;
   }

   async updateHolidayProgram(holidayProgram: HolidayProgramJson): Promise<HolidayProgramJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "holidayProgram/" + holidayProgram.ID, {
         method: "put",
         body: JSON.stringify(holidayProgram),
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
      });

      return (await result.json()) as HolidayProgramJson;
   }

   async addHolidayProgram(holidayProgram: HolidayProgramJson): Promise<HolidayProgramJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "holidayProgram", {
         method: "post",
         body: JSON.stringify(holidayProgram),
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
      });

      return (await result.json()) as HolidayProgramJson;
   }

   async deleteHolidayProgram(id: number): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "holidayProgram/" + id, {
         method: "delete",
         headers: new Headers(this.authHeader)
      });
   }

   async getHolidayProgramWithBookings(id: number): Promise<HolidayProgramWithBookingsJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "holidayProgram/" + id, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as HolidayProgramWithBookingsJson;
   }

   async getHolidayProgramBookingWithProgram(programId: number, bookingId: number): Promise<HolidayProgramWithBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "holidayProgram/" + programId + "/booking/" + bookingId, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as HolidayProgramWithBookingJson;
   }


   async getAggregatedHolidayProgramBookings(states: HolidayProgramBookingStatus[], onlyCurrentPrograms: boolean = false, afterDateTime: Date | null = null): Promise<{ holidayProgramBookings: HolidayProgramWithBookingJson[], moreAvailable: boolean; }> {

      let requestParams = [];

      requestParams.push("states=" + states.join(","));
      requestParams.push("onlyCurrentPrograms=" + (onlyCurrentPrograms ? "1" : "0"));

      if (afterDateTime) requestParams.push("afterDateTime=" + DateFormatter.formatISODateTime(afterDateTime));

      let result = await RestClient.request(RestClient.serverApiUrlBase + "holidayProgram/booking?" + requestParams.join("&"), {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return await result.json();
   }

   async updateHolidayProgramBooking(programId: number, holidayProgramBooking: HolidayProgramBookingJson): Promise<HolidayProgramBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "holidayProgram/" + programId + "/booking/" + holidayProgramBooking.ID, {
         method: "put",
         body: JSON.stringify(holidayProgramBooking),
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
      });
      return (await result.json()) as HolidayProgramBookingJson;
   }

   async addHolidayProgramBooking(programId: number, holidayProgramBooking: HolidayProgramBookingJson): Promise<HolidayProgramBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "holidayProgram/" + programId + "/booking", {
         method: "post",
         body: JSON.stringify(holidayProgramBooking),
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
      });
      return (await result.json()) as HolidayProgramBookingJson;
   }

   async deleteHolidayProgramBooking(programId: number, bookingId: number): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "holidayProgram/" + programId + "/booking/" + bookingId, {
         method: "delete",
         headers: new Headers(this.authHeader)
      });
   }

   async confirmHolidayProgramBooking(programId: number, holidayProgramBooking: HolidayProgramBookingJson): Promise<HolidayProgramBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "holidayProgram/" + programId + "/booking/" + holidayProgramBooking.ID + "/confirm", {
         method: "put",
         body: JSON.stringify(holidayProgramBooking),
         headers: new Headers(this.authHeader),
      });
      return (await result.json()) as HolidayProgramBookingJson;
   }

   async cancelHolidayProgramBooking(programId: number, holidayProgramBooking: HolidayProgramBookingJson): Promise<HolidayProgramBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "holidayProgram/" + programId + "/booking/" + holidayProgramBooking.ID + "/cancel", {
         method: "put",
         body: JSON.stringify(holidayProgramBooking),
         headers: new Headers(this.authHeader),
      });
      return (await result.json()) as HolidayProgramBookingJson;
   }

   async downloadHolidayProgramBookingsListFile(programId: number, sortedByChilds: boolean): Promise<Blob> {
      let requestParams = [];

      requestParams.push("sortedByChilds=" + (sortedByChilds ? "1" : "0"));
      
      let result = await RestClient.request(RestClient.serverApiUrlBase + "holidayProgram/" + programId + "/bookingsList?" + requestParams.join("&"), {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return await result.blob() as any;
   }

   async downloadHolidayProgramSubsidyListFile(programId: number): Promise<Blob> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "holidayProgram/" + programId + "/subsidyListArchive", {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return await result.blob() as any;
   }

   async getWorkshops(): Promise<WorkshopJson[]> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "workshop", {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as WorkshopJson[];
   }

   static async getWorkshopBookingInfo(): Promise<WorkshopInfoJson[]> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "workshop/info", {
         method: "get"
      });

      return (await result.json()) as WorkshopInfoJson[];
   }

   async getWorkshopWithBookings(workshopId: number): Promise<WorkshopWithBookingsJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "workshop/" + workshopId, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as WorkshopWithBookingsJson;
   }

   async deleteWorkshop(workshopId: number): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "workshop/" + workshopId, {
         method: "delete",
         headers: new Headers(this.authHeader)
      });
   }

   async addWorkshop(workshop: WorkshopJson): Promise<WorkshopJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "workshop", {
         method: "post",
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
         body: JSON.stringify(workshop)
      });

      return (await result.json()) as WorkshopJson;
   }

   async updateWorkshop(workshop: WorkshopJson): Promise<WorkshopJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "workshop/" + workshop.ID, {
         method: "put",
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
         body: JSON.stringify(workshop)
      });

      return (await result.json()) as WorkshopJson;
   }

   async getWorkshopBooking(workshopId: number, bookingId: number): Promise<WorkshopWithBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "workshop/" + workshopId + "/booking/" + bookingId, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as WorkshopWithBookingJson;
   }

   async getAggregatedWorkshopBookings(states: WorkshopBookingStatus[], afterDateTime: Date | null = null): Promise<{ workshopBookings: WorkshopWithBookingJson[], moreAvailable: boolean; }> {

      let requestParams = [];

      requestParams.push("states=" + states.join(","));

      if (afterDateTime) requestParams.push("afterDateTime=" + DateFormatter.formatISODateTime(afterDateTime));

      let result = await RestClient.request(RestClient.serverApiUrlBase + "workshop/booking?" + requestParams.join("&"), {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return await result.json();
   }

   static async requestWorkshopBooking(workshopId: number, booking: WorkshopBookingJson): Promise<WorkshopBookingStatus.waitingList | WorkshopBookingStatus.requested> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "workshop/" + workshopId + "/booking/request", {
         method: "post",
         headers: new Headers([['Content-Type', 'application/json']]),
         body: JSON.stringify(booking)
      });
      return (await result.text()) as WorkshopBookingStatus.waitingList | WorkshopBookingStatus.requested;
   }

   async addWorkshopBooking(workshopId: number, booking: WorkshopBookingJson): Promise<WorkshopBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "workshop/" + workshopId + "/booking", {
         method: "post",
         body: JSON.stringify(booking),
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader])
      });

      return (await result.json()) as WorkshopBookingJson;
   }

   async updateWorkshopBooking(workshopId: number, booking: WorkshopBookingJson): Promise<WorkshopBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "workshop/" + workshopId + "/booking/" + booking.ID, {
         method: "put",
         body: JSON.stringify(booking),
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader])
      });
      return (await result.json()) as WorkshopBookingJson;
   }

   async deleteWorkshopBooking(workshopId: number, bookingId: number): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "workshop/" + workshopId + "/booking/" + bookingId, {
         method: "delete",
         headers: new Headers(this.authHeader)
      });
   }

   async confirmWorkshopBooking(workshopId: number, bookingId: number): Promise<WorkshopBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "workshop/" + workshopId + "/booking/" + bookingId + "/confirm", {
         method: "put",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as WorkshopBookingJson;
   }

   async cancelWorkshopBooking(workshopId: number, bookingId: number): Promise<WorkshopBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "workshop/" + workshopId + "/booking/" + bookingId + "/cancel", {
         method: "put",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as WorkshopBookingJson;
   }

   async getGroupBookings(
      states: GroupBookingStatus[] | null = null,
      staffId: number | null = null,
      afterDateTime: Date | null = null,
      beforeDateTime: Date | null = null,
      searchString: string | null = null,
      order: "ASC" | "DESC" | null = "ASC",
      skipAmount: number | null = null): Promise<{ groupBookings: GroupBookingJson[], moreAvailable: boolean; }> {

      let requestParams = [];

      if (states) requestParams.push("states=" + states.join(","));
      if (staffId) requestParams.push("staffId=" + staffId.toString());
      if (afterDateTime) requestParams.push("afterDateTime=" + DateFormatter.formatISODateTime(afterDateTime));
      if (beforeDateTime) requestParams.push("beforeDateTime=" + DateFormatter.formatISODateTime(beforeDateTime));
      if (searchString) requestParams.push("search=" + encodeURIComponent(searchString));
      if (order) requestParams.push("order=" + order);
      if (skipAmount) requestParams.push("skip=" + skipAmount);

      let requestParam = requestParams.length > 0 ? "?" + requestParams.join("&") : "";

      let result = await RestClient.request(RestClient.serverApiUrlBase + "groupBooking" + requestParam, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as { groupBookings: GroupBookingJson[], moreAvailable: boolean; };
   }

   async getGroupBooking(groupBookingId: number): Promise<GroupBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "groupBooking/" + groupBookingId, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as GroupBookingJson;
   }


   static async getGroupBookingInfo(): Promise<GroupBookingInfoJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "groupBooking/info", {
         method: "get"
      });

      return (await result.json()) as GroupBookingInfoJson;
   }

   async deleteGroupBooking(groupBookingId: number): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "groupBooking/" + groupBookingId, {
         method: "delete",
         headers: new Headers(this.authHeader)
      });
   }

   async addGroupBooking(groupBooking: GroupBookingJson): Promise<GroupBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "groupBooking", {
         method: "post",
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
         body: JSON.stringify(groupBooking)
      });

      return (await result.json()) as GroupBookingJson;
   }

   static async requestGroupBooking(groupBooking: GroupBookingJson) {
      await RestClient.request(RestClient.serverApiUrlBase + "groupBooking/request", {
         method: "post",
         headers: new Headers([['Content-Type', 'application/json']]),
         body: JSON.stringify(groupBooking)
      });
   }

   async updateGroupBooking(groupBooking: GroupBookingJson): Promise<GroupBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "groupBooking/" + groupBooking.ID, {
         method: "put",
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
         body: JSON.stringify(groupBooking)
      });

      return (await result.json()) as GroupBookingJson;
   }

   async getGroupBookingPrograms(): Promise<GroupBookingProgramJson[]> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "groupBooking/program", {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as GroupBookingProgramJson[];
   }

   async getGroupBookingProgram(groupBookingProgramId: number): Promise<GroupBookingProgramJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "groupBooking/program/" + groupBookingProgramId, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as GroupBookingProgramJson;
   }

   async deleteGroupBookingProgram(groupBookingProgramId: number): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "groupBooking/program/" + groupBookingProgramId, {
         method: "delete",
         headers: new Headers(this.authHeader)
      });
   }

   async addGroupBookingProgram(groupBookingProgram: GroupBookingProgramJson): Promise<GroupBookingProgramJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "groupBooking/program", {
         method: "post",
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
         body: JSON.stringify(groupBookingProgram)
      });

      return (await result.json()) as GroupBookingProgramJson;
   }

   async updateGroupBookingProgram(groupBookingProgram: GroupBookingProgramJson): Promise<GroupBookingProgramJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "groupBooking/program/" + groupBookingProgram.ID, {
         method: "put",
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
         body: JSON.stringify(groupBookingProgram)
      });

      return (await result.json()) as GroupBookingProgramJson;
   }

   async offerGroupBooking(groupBookingId: number): Promise<GroupBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "groupBooking/" + groupBookingId + "/offer", {
         method: "put",
         headers: new Headers(this.authHeader),
      });

      return (await result.json()) as GroupBookingJson;
   }

   async confirmGroupBooking(groupBookingId: number): Promise<GroupBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "groupBooking/" + groupBookingId + "/confirm", {
         method: "put",
         headers: new Headers(this.authHeader),
      });

      return (await result.json()) as GroupBookingJson;
   }

   async confirmCancelGroupBooking(groupBookingId: number): Promise<GroupBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "groupBooking/" + groupBookingId + "/confirmCancel", {
         method: "put",
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
      });

      return (await result.json()) as GroupBookingJson;
   }

   async cancelGroupBooking(groupBookingId: number): Promise<GroupBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "groupBooking/" + groupBookingId + "/cancel", {
         method: "put",
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
      });

      return (await result.json()) as GroupBookingJson;
   }

   static async getGroupBookingUsingToken(groupBookingId: number, token: string): Promise<GroupBookingCustomerViewJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "groupBooking/" + groupBookingId + "/token", {
         method: "get",
         headers: new Headers([['Authorization', 'Bearer ' + token]])
      });

      return (await result.json()) as GroupBookingCustomerViewJson;
   }

   static async acceptGroupBookingUsingToken(groupBookingId: number, token: string): Promise<GroupBookingCustomerViewJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "groupBooking/" + groupBookingId + "/token/accept", {
         method: "put",
         headers: new Headers([['Authorization', 'Bearer ' + token]])
      });

      return (await result.json()) as GroupBookingCustomerViewJson;
   }

   static async cancelGroupBookingUsingToken(groupBooking: GroupBookingCustomerViewJson, groupBookingId: number, token: string): Promise<GroupBookingCustomerViewJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "groupBooking/" + groupBookingId + "/token/cancel", {
         method: "put",
         body: JSON.stringify(groupBooking),
         headers: new Headers([['Content-Type', 'application/json'], ['Authorization', 'Bearer ' + token]])
      });

      return (await result.json()) as GroupBookingCustomerViewJson;
   }

   static async requestMember(member: MemberJson): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "member/request", {
         method: "post",
         headers: new Headers([['Content-Type', 'application/json']]),
         body: JSON.stringify(member)
      });
   }

   async createLinkedMailingRecipientForMember(memberId: number): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "member/" + memberId + "/mailingRecipient", {
         method: "post",
         headers: new Headers(this.authHeader),
      });
   }

   async confirmMember(memberId: number): Promise<MemberJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "member/" + memberId + "/confirm", {
         method: "put",
         headers: new Headers(this.authHeader),
      });

      return (await result.json()) as MemberJson;
   }

   async getMembers(states: MemberStatus[] | null = null): Promise<MemberJson[]> {
      let requestParams = [];

      if (states) requestParams.push("states=" + states.join(","));

      let requestParam = requestParams.length > 0 ? "?" + requestParams.join("&") : "";

      let result = await RestClient.request(RestClient.serverApiUrlBase + "member" + requestParam, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as MemberJson[];
   }

   async getMember(id: number): Promise<MemberJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "member/" + id, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as MemberJson;
   }
   async deleteMember(member: MemberJson): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "member/" + member.ID, {
         method: "delete",
         headers: new Headers(this.authHeader)
      });
   }

   async updateMember(member: MemberJson): Promise<MemberJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "member/" + member.ID, {
         method: "put",
         body: JSON.stringify(member),
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
      });

      return (await result.json()) as MemberJson;
   }

   async addMember(member: MemberJson): Promise<MemberJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "member/", {
         method: "post",
         body: JSON.stringify(member),
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
      });

      return (await result.json()) as MemberJson;
   }

   async downloadMemberListArchive(
      type: MembershipType | null = null,
      state: MemberStatus | null = null,
      renew: boolean | null = null
   ): Promise<Blob> {
      let requestParams = [];

      if (type !== null) requestParams.push("type=" + type);
      if (state !== null) requestParams.push("state=" + state);
      if (renew !== null) requestParams.push("renew=" + renew);

      let requestParam = requestParams.length > 0 ? "?" + requestParams.join("&") : "";

      let result = await RestClient.request(RestClient.serverApiUrlBase + "member/listArchive" + requestParam, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return await result.blob() as any;
   }


   async exportPaymentsForMemberships(): Promise<PaymentExportInfoJson | null> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "payment/member/renew/export", {
         method: "post",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as PaymentExportInfoJson | null;
   }

   async getMembershipsReadyForPaymentExport(): Promise<{ term: MembershipTermJson, member: MemberJson; }[]> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "payment/member/renew", {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as { term: MembershipTermJson, member: MemberJson; }[];
   }

   async extendMembership(member: MemberJson): Promise<MemberJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "member/" + member.ID + "/extend", {
         method: "put",
         headers: new Headers(this.authHeader),
      });

      return (await result.json()) as MemberJson;
   }

   async addMailingRecipient(clientMailingJson: ClientMailingJson): Promise<ClientMailingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "mailing/recipient", {
         method: "post",
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
         body: JSON.stringify(clientMailingJson)
      });

      return (await result.json()) as ClientMailingJson;
   }

   static async requestMailingRecipient(clientMailingJson: ClientMailingJson): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "mailing/recipient/request", {
         method: "post",
         headers: new Headers([['Content-Type', 'application/json']]),
         body: JSON.stringify(clientMailingJson)
      });
   }

   async getMailingRecipients(
      states: MailingRecipientStatus[] | null = null,
      mailingListId: number | null = null,
      typeIds: number[] | null = null,
      searchString: string | null = null,
      skipAmount: number | null = null,
      loadAll: boolean = false): Promise<{ mailingRecipients: ClientMailingJson[], moreAvailable: boolean; }> {

      let requestParams = [];

      if (states) requestParams.push("states=" + states.join(","));
      if (mailingListId) requestParams.push("mailingListId=" + mailingListId);
      if (typeIds) requestParams.push("typeIds=" + typeIds.join(","));
      if (searchString) requestParams.push("search=" + encodeURIComponent(searchString));
      if (skipAmount) requestParams.push("skip=" + skipAmount);

      requestParams.push("loadAll=" + (loadAll ? "1" : "0"));

      let requestParam = requestParams.length > 0 ? "?" + requestParams.join("&") : "";

      let result = await RestClient.request(RestClient.serverApiUrlBase + "mailing/recipient" + requestParam, {
         method: "get",
         headers: new Headers(this.authHeader),
      });

      return (await result.json()) as { mailingRecipients: ClientMailingJson[], moreAvailable: false; };
   }

   async getMailingRecipient(clientId: number): Promise<ClientMailingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "mailing/recipient/" + clientId, {
         method: "get",
         headers: new Headers(this.authHeader),
      });

      return (await result.json()) as ClientMailingJson;
   }

   async deleteMailingRecipient(clientId: number): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "mailing/recipient/" + clientId, {
         method: "delete",
         headers: new Headers(this.authHeader)
      });
   }

   async resendMailingRecipientDoubleOptInMail(clientId: number): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "mailing/recipient/" + clientId + "/resendDoubleOptInMail", {
         method: "put",
         headers: new Headers(this.authHeader)
      });
   }


   async updateMailingRecipient(clientMailingJson: ClientMailingJson): Promise<ClientMailingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "mailing/recipient/" + clientMailingJson.ID, {
         method: "put",
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
         body: JSON.stringify(clientMailingJson)
      });
      return (await result.json()) as ClientMailingJson;
   }

   static async unsubscribeMailingRecipientUsingToken(clientId: number, token: string): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "mailing/recipient/" + clientId + "/token/unsubscribe", {
         method: "put",
         headers: new Headers([['Authorization', 'Bearer ' + token]])
      });
   }

   static async confirmMailingRecipientUsingToken(clientId: number, token: string): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "mailing/recipient/" + clientId + "/token/confirm", {
         method: "put",
         headers: new Headers([['Authorization', 'Bearer ' + token]])
      });
   }

   static async getMailingRecipientInfoUsingToken(clientId: number, token: string): Promise<ClientMailingCustomerInfoJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "mailing/recipient/" + clientId + "/token/info", {
         method: "get",
         headers: new Headers([['Authorization', 'Bearer ' + token]])
      });

      return (await result.json()) as ClientMailingCustomerInfoJson;
   }

   static async getMailingListsSignUpPublic(): Promise<MailingListJson[]> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "mailing/list/signUpPublic", {
         method: "get"
      });

      return (await result.json()) as MailingListJson[];
   }

   async getMailingLists(): Promise<MailingListJson[]> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "mailing/list", {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as MailingListJson[];
   }

   async getMailingList(mailingListId: number): Promise<MailingListJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "mailing/list/" + mailingListId, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as MailingListJson;
   }

   async deleteMailingList(mailingListId: number): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "mailing/list/" + mailingListId, {
         method: "delete",
         headers: new Headers(this.authHeader)
      });
   }

   async addMailingList(mailingListJson: MailingListJson): Promise<MailingListJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "mailing/list", {
         method: "post",
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
         body: JSON.stringify(mailingListJson)
      });

      return (await result.json()) as MailingListJson;
   }

   async updateMailingList(mailingListJson: MailingListJson): Promise<MailingListJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "mailing/list/" + mailingListJson.ID, {
         method: "put",
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
         body: JSON.stringify(mailingListJson)
      });
      return (await result.json()) as MailingListJson;
   }

   async getMailingMessages(skip: number = 0): Promise<{ messages: MailingMessageJson[], moreAvailable: false; }> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "mailing/message?skip=" + skip, {
         method: "get",
         headers: new Headers(this.authHeader)
      });
      return (await result.json()) as { messages: MailingMessageJson[], moreAvailable: false; };
   }

   async getMailingMessage(messageId: number): Promise<MailingMessageJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "mailing/message/" + messageId, {
         method: "get",
         headers: new Headers(this.authHeader)
      });
      return (await result.json()) as MailingMessageJson;
   }

   async getMailingMessageRecipientInfos(messageId: number): Promise<MailingMessageRecipientInfoJson[]> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "mailing/message/" + messageId + "/recipient", {
         method: "get",
         headers: new Headers(this.authHeader)
      });
      return (await result.json()) as MailingMessageRecipientInfoJson[];
   }

   async getMailingMessageRecipientInfo(messageId: number, recipientInfoId: number): Promise<MailingMessageRecipientInfoJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "mailing/message/" + messageId + "/recipient/" + recipientInfoId, {
         method: "get",
         headers: new Headers(this.authHeader)
      });
      return (await result.json()) as MailingMessageRecipientInfoJson;
   }

   async resendMailingMessageRecipientInfo(messageId: number, recipientInfoId: number): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "mailing/message/" + messageId + "/recipient/" + recipientInfoId + "/resend", {
         method: "put",
         headers: new Headers(this.authHeader)
      });
   }

   async downloadMailingMessageAttachment(messageId: number, attachment: MailingMessageAttachmentJson): Promise<ArrayBuffer> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "mailing/message/" + messageId + "/attachment/" + attachment.ID, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return await result.arrayBuffer();
   }

   async sendMailingMessage(mailingMessageJson: MailingMessageJson): Promise<MailingMessageJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "mailing/message", {
         method: "post",
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
         body: JSON.stringify(mailingMessageJson)
      });

      return (await result.json()) as MailingMessageJson;
   }

   async deleteMailingMessage(mailingMessageId: number): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "mailing/message/" + mailingMessageId, {
         method: "delete",
         headers: new Headers(this.authHeader)
      });
   }

   async getRentalItems(): Promise<RentalItemJson[]> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/item", {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as RentalItemJson[];
   }

   async getRentalItem(rentalItemId: number): Promise<RentalItemJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/item/" + rentalItemId, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as RentalItemJson;
   }

   static async getRentalPickupReturnSlots(weekDay: WeekDay): Promise<RentalTimeSlotJson[]> {
      let params = "?weekDay=" + weekDay;

      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/pickupReturnSlots" + params, {
         method: "get",
      });

      return (await result.json()) as RentalTimeSlotJson[];
   }

   static async getRentalBookingInfo(): Promise<RentalBookingInfoJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/info", {
         method: "get"
      });

      return (await result.json()) as RentalBookingInfoJson;
   }

   static async getRentalItemsInfo(): Promise<RentalItemInfoJson[]> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/item/info", {
         method: "get"
      });

      return (await result.json()) as RentalItemJson[];
   }

   static async getRentalItemsInfoClient(username: string, password: string): Promise<RentalItemInfoJson[]> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/item/info", {
         method: "get",
         headers: new Headers([...this.buildAuthHeader(username, password), ['Content-Type', 'application/json']]),
      });

      return (await result.json()) as RentalItemJson[];
   }

   static getRentalItemThumbnailUrl(rentalItemId: number): string {
      return RestClient.serverApiUrlBase + "rental/item/" + rentalItemId + "/thumbnail";
   }

   static async getRentalItemInfo(rentalItemId: number): Promise<RentalItemInfoJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/item/" + rentalItemId + "/info", {
         method: "get"
      });

      return (await result.json()) as RentalItemJson;
   }

   static async getRentalItemAvailableDayAmounts(rentalItemId: number, fromDate: Date, toDate: Date, ignoreRequestBookingLimits: boolean = false, ignoreBookingId: number | null = null): Promise<{ [index: string]: {"open": boolean, "contactlessReturn": boolean; "amount": number} }> {
      let query = "?fromDate=" + DateFormatter.formatISODate(fromDate).split("T")[0] +
         "&toDate=" + DateFormatter.formatISODate(toDate).split("T")[0] +
         "&ignoreLimits=" + (ignoreRequestBookingLimits ? "1" : "0");

      if(ignoreBookingId) query += "&ignoreBookingId=" + ignoreBookingId;

      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/item/" + rentalItemId + "/availability" + query, {
         method: "get"
      });

      return (await result.json()) as { [index: string]: {"open": boolean, "contactlessReturn": boolean; "amount": number} };
   }

   static async getRentalItemAvailableConditions(rentalItemId: number, requiredAmount: number, fromDate: Date, toDate: Date, ignoreRequestBookingLimits: boolean = false, ignoreBookingId: number | null = null): Promise<RentalConditionJson[]> {
      
      let query = "?amount=" + requiredAmount +
         "&fromDate=" + DateFormatter.formatISODate(fromDate).split("T")[0] +
         "&toDate=" + DateFormatter.formatISODate(toDate).split("T")[0] +
         "&ignoreLimits=" + (ignoreRequestBookingLimits ? "1" : "0");

         if(ignoreBookingId) query += "&ignoreBookingId=" + ignoreBookingId;

      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/item/" + rentalItemId + "/availability/condition" + query, {
         method: "get"
      });

      return (await result.json()) as RentalConditionJson[];
   }

   static async getRentalItemAvailablePickupDays(rentalItemId: number, rentalConditionId: number, requiredAmount: number, fromDate: Date, toDate: Date, ignoreRequestBookingLimits: boolean = false, ignoreBookingId: number | null = null): Promise<string[]> {
      let query = "?rentalConditionId=" + rentalConditionId +
         "&amount=" + requiredAmount +
         "&fromDate=" + DateFormatter.formatISODate(fromDate).split("T")[0] +
         "&toDate=" + DateFormatter.formatISODate(toDate).split("T")[0] +
         "&ignoreLimits=" + (ignoreRequestBookingLimits ? "1" : "0");

         if(ignoreBookingId) query += "&ignoreBookingId=" + ignoreBookingId;

      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/item/" + rentalItemId + "/availability/pickup" + query, {
         method: "get"
      });

      return (await result.json()) as string[];
   }

   static async getRentalItemAvailableReturnDays(rentalItemId: number, rentalConditionId: number, requiredAmount: number, pickupDate: Date, toDate: Date, ignoreRequestBookingLimits: boolean = false, ignoreBookingId: number | null = null): Promise<string[]> {
      let query = "?rentalConditionId=" + rentalConditionId +
         "&amount=" + requiredAmount +
         "&pickupDate=" + DateFormatter.formatISODate(pickupDate).split("T")[0] +
         "&toDate=" + DateFormatter.formatISODate(toDate).split("T")[0] +
         "&ignoreLimits=" + (ignoreRequestBookingLimits ? "1" : "0");

      if(ignoreBookingId) query += "&ignoreBookingId=" + ignoreBookingId;

      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/item/" + rentalItemId + "/availability/return" + query, {
         method: "get"
      });

      return (await result.json()) as string[];
   }

   async addRentalItem(rentalItemJson: RentalItemJson): Promise<RentalItemJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/item", {
         method: "post",
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
         body: JSON.stringify(rentalItemJson)
      });

      return (await result.json()) as RentalItemJson;
   }

   async updateRentalItem(rentalItemJson: RentalItemJson): Promise<RentalItemJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/item/" + rentalItemJson.ID, {
         method: "put",
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
         body: JSON.stringify(rentalItemJson)
      });

      return (await result.json()) as RentalItemJson;
   }

   async deleteRentalItem(rentalItemId: number): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "rental/item/" + rentalItemId, {
         method: "delete",
         headers: new Headers(this.authHeader)
      });
   }

   async getRentalAccounts(): Promise<RentalAccountJson[]> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/account", {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as RentalAccountJson[];
   }

   async getRentalAccount(id: number): Promise<RentalAccountJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/account/" + id, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as RentalAccountJson;
   }

   async updateRentalAccount(user: RentalAccountJson): Promise<RentalAccountJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/account/" + user.ID, {
         method: "put",
         headers: new Headers(this.authHeader.concat([['Content-Type', 'application/json']])),
         body: JSON.stringify(user),
      });

      return (await result.json()) as RentalAccountJson;
   }

   async addRentalAccount(user: RentalAccountJson): Promise<RentalAccountJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/account", {
         method: "post",
         headers: new Headers(this.authHeader.concat([['Content-Type', 'application/json']])),
         body: JSON.stringify(user)
      });

      return (await result.json()) as RentalAccountJson;
   }

   async deleteRentalAccount(id: number): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "rental/account/" + id, {
         method: "delete",
         headers: new Headers(this.authHeader)
      });
   }

   async rentalAccountChangePassword(id: number, password: string): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "rental/account/" + id + "/password", {
         method: "put",
         headers: new Headers(this.authHeader),
         body: password
      });
   }

   static async loginRentalAccountGetInfo(email: string, password: string): Promise<RentalAccountInfoJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/account/info", {
         method: "get",
         headers: new Headers(this.buildAuthHeader(email, password))
      });

      return (await result.json()) as RentalAccountInfoJson;
   }

   async getRentalBookings(
      states: RentalBookingStatus[] | null = null,
      rentalItemId: number | null = null,
      afterDate: Date | null = null,
      beforeDate: Date | null = null,
      searchString: string | null = null,
      skipAmount: number | null = null): Promise<{ rentalBookings: RentalBookingJson[], moreAvailable: boolean; }> {

      let requestParams = [];

      if (states) requestParams.push("states=" + states.join(","));
      if (rentalItemId) requestParams.push("rentalItemId=" + rentalItemId);
      if (afterDate) requestParams.push("afterDate=" + DateFormatter.formatISODate(afterDate));
      if (beforeDate) requestParams.push("beforeDate=" + DateFormatter.formatISODate(beforeDate));
      if (searchString) requestParams.push("search=" + encodeURIComponent(searchString));
      if (skipAmount) requestParams.push("skip=" + skipAmount);

      let requestParam = requestParams.length > 0 ? "?" + requestParams.join("&") : "";

      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/booking" + requestParam, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as { rentalBookings: RentalBookingJson[], moreAvailable: boolean; };
   }

   async getRentalBooking(id: number): Promise<RentalBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/booking/" + id, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as RentalBookingJson;
   }

   async updateRentalBooking(user: RentalBookingJson): Promise<RentalBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/booking/" + user.ID, {
         method: "put",
         headers: new Headers(this.authHeader.concat([['Content-Type', 'application/json']])),
         body: JSON.stringify(user),
      });

      return (await result.json()) as RentalBookingJson;
   }

   async addRentalBooking(user: RentalBookingJson): Promise<RentalBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/booking", {
         method: "post",
         headers: new Headers(this.authHeader.concat([['Content-Type', 'application/json']])),
         body: JSON.stringify(user)
      });

      return (await result.json()) as RentalBookingJson;
   }

   async deleteRentalBooking(id: number): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "rental/booking/" + id, {
         method: "delete",
         headers: new Headers(this.authHeader)
      });
   }

   static async requestRentalBookingsRentalAccount(rentalBookings: RentalBookingJson[], username: string, password: string, restrictToTypeId: number | null = null): Promise<void> {
      let requestParams = [];

      if (restrictToTypeId) requestParams.push("restrictToTypeId=" + restrictToTypeId);

      let requestParam = requestParams.length > 0 ? "?" + requestParams.join("&") : "";
      
      await RestClient.request(RestClient.serverApiUrlBase + "rental/booking/request" + requestParam, {
         method: "post",
         headers: new Headers([...this.buildAuthHeader(username, password), ['Content-Type', 'application/json']]),
         body: JSON.stringify({ bookings: rentalBookings })
      });
   }

   static async requestRentalBookings(rentalBookings: RentalBookingJson[], restrictToTypeId: number | null = null): Promise<void> {
      let requestParams = [];

      if (restrictToTypeId) requestParams.push("restrictToTypeId=" + restrictToTypeId);

      let requestParam = requestParams.length > 0 ? "?" + requestParams.join("&") : "";
      
      await RestClient.request(RestClient.serverApiUrlBase + "rental/booking/request" + requestParam, {
         method: "post",
         headers: new Headers([['Content-Type', 'application/json']]),
         body: JSON.stringify({ bookings: rentalBookings })
      });
   }

   async confirmRentalBooking(rentalBookingId: number): Promise<RentalBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/booking/" + rentalBookingId + "/confirm", {
         method: "put",
         headers: new Headers(this.authHeader),
      });

      return (await result.json()) as RentalBookingJson;
   }

   async setRentalBookingCollected(rentalBookingId: number): Promise<RentalBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/booking/" + rentalBookingId + "/collect", {
         method: "put",
         headers: new Headers(this.authHeader),
      });

      return (await result.json()) as RentalBookingJson;
   }

   async setRentalBookingReturned(rentalBookingId: number): Promise<RentalBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/booking/" + rentalBookingId + "/return", {
         method: "put",
         headers: new Headers(this.authHeader),
      });

      return (await result.json()) as RentalBookingJson;
   }

   async setRentalBookingReadyForPayment(rentalBookingId: number): Promise<RentalBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/booking/" + rentalBookingId + "/readyForPayment", {
         method: "put",
         headers: new Headers(this.authHeader),
      });

      return (await result.json()) as RentalBookingJson;
   }

   async getRentalBookingsReadyForPaymentExport(): Promise<RentalBookingJson[]> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "payment/rental", {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as RentalBookingJson[];
   }

   async exportPaymentsForRentalBookings(): Promise<PaymentExportInfoJson | null> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "payment/rental/export", {
         method: "post",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as PaymentExportInfoJson | null;
   }

   async cancelRentalBooking(rentalId: number): Promise<RentalBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/booking/" + rentalId + "/cancel", {
         method: "put",
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader]),
      });

      return (await result.json()) as RentalBookingJson;
   }

   static async getRentalBookingUsingToken(id: number, token: string): Promise<RentalBookingCustomerViewJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/booking/" + id + "/token", {
         method: "get",
         headers: new Headers([['Authorization', 'Bearer ' + token]])
      });

      return (await result.json()) as RentalBookingCustomerViewJson;
   }

   static async cancelRentalBookingUsingToken(id: number, token: string): Promise<RentalBookingCustomerViewJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/booking/" + id + "/token/cancel", {
         method: "put",
         headers: new Headers([['Authorization', 'Bearer ' + token]])
      });

      return (await result.json()) as RentalBookingCustomerViewJson;
   }

   async getRentalStatistics(
      afterDateTime: Date | null = null,
      beforeDateTime: Date | null = null,): Promise<{ ID: number, name: string, count: string; }[]> {

      let requestParams = [];

      if (afterDateTime) requestParams.push("afterDateTime=" + DateFormatter.formatISODateTime(afterDateTime));
      if (beforeDateTime) requestParams.push("beforeDateTime=" + DateFormatter.formatISODateTime(beforeDateTime));

      let requestParam = requestParams.length > 0 ? "?" + requestParams.join("&") : "";

      let result = await RestClient.request(RestClient.serverApiUrlBase + "rental/stats" + requestParam, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as { ID: number, name: string, count: string; }[];
   }

   async getClients(
      typeIds: number[] | null = null,
      onlyCreatedByManagement: boolean = false, 
      onlyWithLogin: boolean = false, 
      includeClientMailings: boolean = false, 
      searchString: string | null = null,
      skipAmount: number | null = null): Promise<{ clients: ClientJson[], moreAvailable: boolean; }> {

      let requestParams = [];

      if (typeIds) requestParams.push("typeIds=" + typeIds.join(","));
      requestParams.push("onlyCreatedByManagement=" + (onlyCreatedByManagement ? "1" : "0"));
      requestParams.push("onlyWithLogin=" + (onlyWithLogin ? "1" : "0"));
      requestParams.push("includeClientMailings=" + (includeClientMailings ? "1" : "0"));
      if (searchString) requestParams.push("search=" + encodeURIComponent(searchString));
      if (skipAmount) requestParams.push("skip=" + skipAmount);

      let requestParam = requestParams.length > 0 ? "?" + requestParams.join("&") : "";

      let result = await RestClient.request(RestClient.serverApiUrlBase + "client" + requestParam, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as { clients: ClientJson[], moreAvailable: boolean; };
   }

   async getClient(id: number): Promise<ClientJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "client/" + id, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as ClientJson;
   }

   async updateClient(client: ClientJson): Promise<ClientJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "client/" + client.ID, {
         method: "put",
         headers: new Headers(this.authHeader.concat([['Content-Type', 'application/json']])),
         body: JSON.stringify(client),
      });

      return (await result.json()) as ClientJson;
   }

   async addClient(client: ClientJson): Promise<ClientJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "client", {
         method: "post",
         headers: new Headers(this.authHeader.concat([['Content-Type', 'application/json']])),
         body: JSON.stringify(client)
      });

      return (await result.json()) as ClientJson;
   }

   async deleteClient(id: number): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "client/" + id, {
         method: "delete",
         headers: new Headers(this.authHeader)
      });
   }

   async clientChangePassword(id: number, password: string): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "client/" + id + "/password", {
         method: "put",
         headers: new Headers(this.authHeader),
         body: password
      });
   }

   static async loginClient(username: string, password: string): Promise<ClientJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "client/account/login", {
         method: "get",
         headers: new Headers(this.buildAuthHeader(username, password))
      });

      return (await result.json()) as ClientJson;
   }

   static async requestClient(client: ClientJson, password: string): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "client/request", {
         method: "post",
         headers: new Headers([['Content-Type', 'application/json']]),
         body: JSON.stringify({client: client, password: password})
      });
   }

   static async updateOwnClient(client: ClientJson, username: string, password: string): Promise<ClientJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "client/account/update", {
         method: "put",
         headers: new Headers([...this.buildAuthHeader(username, password), ['Content-Type', 'application/json']]),
         body: JSON.stringify(client)
      });

      return (await result.json()) as ClientJson;
   }

   static async deleteOwnClient(username: string, password: string): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "client/account/delete", {
         method: "delete",
         headers: new Headers(this.buildAuthHeader(username, password))
      });
   }

   static async clientChangeOwnPassword(newPassword: string, username: string, password: string): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "client/account/changePassword", {
         method: "put",
         headers: new Headers(this.buildAuthHeader(username, password)),
         body: newPassword
      });
   }

   static async confirmClientUsingToken(clientId: number, token: string): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "client/" + clientId + "/token/confirm", {
         method: "put",
         headers: new Headers([['Authorization', 'Bearer ' + token]])
      });
   }

   static async resendClientConfirmTokenMail(username: string, password: string): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "client/account/resendClientConfirmTokenMail", {
         method: "put",
         headers: new Headers(this.buildAuthHeader(username, password))
      });
   }

   static async confirmClient(username: string, password: string): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "client/account/confirm", {
         method: "put",
         headers: new Headers(this.buildAuthHeader(username, password))
      });
   }

   static async getUsernameAvailability(username: string): Promise<boolean> {
      let query = "?username=" + username;

      let result = await RestClient.request(RestClient.serverApiUrlBase + "client/username/availability" + query, {
         method: "get"
      });

      return (await result.json()) as boolean;
   }

   async getEvents(
      states: EventStatus[] | null = null,
      onlyUpcoming: boolean = false, 
      onlyRegistrationAllowed: boolean = false, 
      onlyInternal: boolean = false, 
      restrictToTypes: number[] | null = null,
      afterDateTime: Date | null = null,
      beforeDateTime: Date | null = null,
      searchString: string | null = null,
      getNotScheduled: boolean = false, 
      skipAmount: number | null = null): Promise<{ events: EventJson[], moreAvailable: boolean; }> {

      let requestParams = [];

      if (states) requestParams.push("states=" + states.join(","));
      requestParams.push("onlyUpcoming=" + (onlyUpcoming ? "1" : "0"));
      requestParams.push("onlyRegistrationAllowed=" + (onlyRegistrationAllowed ? "1" : "0"));
      requestParams.push("onlyInternal=" + (onlyInternal ? "1" : "0"));
      if (restrictToTypes) requestParams.push("restrictToTypes=" + restrictToTypes.join(","));
      if (afterDateTime) requestParams.push("afterDateTime=" + DateFormatter.formatISODateTime(afterDateTime));
      if (beforeDateTime) requestParams.push("beforeDateTime=" + DateFormatter.formatISODateTime(beforeDateTime));
      if (searchString) requestParams.push("search=" + encodeURIComponent(searchString));
      requestParams.push("getNotScheduled=" + (getNotScheduled ? "1" : "0"));
      if (skipAmount) requestParams.push("skip=" + skipAmount);

      let requestParam = requestParams.length > 0 ? "?" + requestParams.join("&") : "";

      let result = await RestClient.request(RestClient.serverApiUrlBase + "event/event" + requestParam, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as { events: EventJson[], moreAvailable: boolean; };
   }

   async getEvent(id: number): Promise<EventJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "event/event/" + id, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as EventJson;
   }

   async updateEvent(event: EventJson): Promise<EventJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "event/event/" + event.ID, {
         method: "put",
         headers: new Headers(this.authHeader.concat([['Content-Type', 'application/json']])),
         body: JSON.stringify(event),
      });

      return (await result.json()) as EventJson;
   }

   async addEvent(event: EventJson): Promise<EventJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "event/event", {
         method: "post",
         headers: new Headers(this.authHeader.concat([['Content-Type', 'application/json']])),
         body: JSON.stringify(event)
      });

      return (await result.json()) as EventJson;
   }

   async deleteEvent(id: number): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "event/event/" + id, {
         method: "delete",
         headers: new Headers(this.authHeader)
      });
   }

   async getEventTemplates(
      states: EventTemplateStatus[] | null = null,
      searchString: string | null = null,
      skipAmount: number | null = null): Promise<{ eventTemplates: EventTemplateJson[], moreAvailable: boolean; }> {

      let requestParams = [];

      if (states) requestParams.push("states=" + states.join(","));
      if (searchString) requestParams.push("search=" + encodeURIComponent(searchString));
      if (skipAmount) requestParams.push("skip=" + skipAmount);

      let requestParam = requestParams.length > 0 ? "?" + requestParams.join("&") : "";

      let result = await RestClient.request(RestClient.serverApiUrlBase + "event/template" + requestParam, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as { eventTemplates: EventTemplateJson[], moreAvailable: boolean; };
   }

   async getEventTemplate(id: number): Promise<EventTemplateJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "event/template/" + id, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as EventTemplateJson;
   }

   async updateEventTemplate(eventTemplate: EventTemplateJson): Promise<EventTemplateJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "event/template/" + eventTemplate.ID, {
         method: "put",
         headers: new Headers(this.authHeader.concat([['Content-Type', 'application/json']])),
         body: JSON.stringify(eventTemplate),
      });

      return (await result.json()) as EventTemplateJson;
   }

   async addEventTemplate(eventTemplate: EventTemplateJson): Promise<EventTemplateJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "event/template", {
         method: "post",
         headers: new Headers(this.authHeader.concat([['Content-Type', 'application/json']])),
         body: JSON.stringify(eventTemplate)
      });

      return (await result.json()) as EventTemplateJson;
   }

   async deleteEventTemplate(id: number): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "event/template/" + id, {
         method: "delete",
         headers: new Headers(this.authHeader)
      });
   }

   static async getEventsInfoClient(username: string, password: string, getNotScheduled: boolean = false): Promise<EventsInfoJson[]> {
      let requestParam = "?getNotScheduled=" + (getNotScheduled ? "1" : "0");
      
      let result = await RestClient.request(RestClient.serverApiUrlBase + "event/info" + requestParam, {
         method: "get",
         headers: new Headers([...this.buildAuthHeader(username, password), ['Content-Type', 'application/json']]),
      });

      return (await result.json()) as EventsInfoJson[];
   }

   static async getEventsInfo(getNotScheduled: boolean = false): Promise<EventsInfoJson[]> {
      let requestParam = "?getNotScheduled=" + (getNotScheduled ? "1" : "0");
      
      let result = await RestClient.request(RestClient.serverApiUrlBase + "event/info" + requestParam, {
         method: "get"
      });

      return (await result.json()) as EventsInfoJson[];
   }

   static getEventThumbnailUrl(eventId: number): string {
      return RestClient.serverApiUrlBase + "event/event/" + eventId + "/thumbnail";
   }

   async getEventWithBookings(eventId: number): Promise<EventWithBookingsJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "event/event/" + eventId, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as EventWithBookingsJson;
   }

   async getEventBooking(eventId: number, bookingId: number): Promise<EventWithBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "event/event/" + eventId + "/booking/" + bookingId, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as EventWithBookingJson;
   }

   async getAggregatedEventBookings(states: EventBookingStatus[], bookedAfterDateTime: Date | null = null, afterDateTime: Date | null = null, beforeDateTime: Date | null = null): Promise<{ eventBookings: EventWithBookingJson[], moreAvailable: boolean; }> {

      let requestParams = [];

      requestParams.push("states=" + states.join(","));

      if (bookedAfterDateTime) requestParams.push("bookedAfterDateTime=" + DateFormatter.formatISODateTime(bookedAfterDateTime));
      if (afterDateTime) requestParams.push("afterDateTime=" + DateFormatter.formatISODateTime(afterDateTime));
      if (beforeDateTime) requestParams.push("beforeDateTime=" + DateFormatter.formatISODateTime(beforeDateTime));

      let result = await RestClient.request(RestClient.serverApiUrlBase + "event/event/booking?" + requestParams.join("&"), {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return await result.json();
   }

   async getEventAppointments(
      searchString: string | null = null, 
      afterDateTime: Date | null = null): Promise<EventAppointmentJson[]> {

      let requestParams = [];
      
      if (searchString) requestParams.push("search=" + encodeURIComponent(searchString));
      if (afterDateTime) requestParams.push("afterDateTime=" + DateFormatter.formatISODateTime(afterDateTime));

      let result = await RestClient.request(RestClient.serverApiUrlBase + "event/event/appointment?" + requestParams.join("&"), {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return await result.json();
   }

   static async requestEventBookingClient(eventId: number, booking: EventBookingJson, username: string, password: string): Promise<EventBookingStatus.waitingList | EventBookingStatus.requested> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "event/event/" + eventId + "/booking/request", {
         method: "post",
         headers: new Headers([...this.buildAuthHeader(username, password), ['Content-Type', 'application/json']]),
         body: JSON.stringify(booking)
      });
      return (await result.text()) as EventBookingStatus.waitingList | EventBookingStatus.requested;
   }

   static async requestEventBooking(eventId: number, booking: EventBookingJson): Promise<EventBookingStatus.waitingList | EventBookingStatus.requested> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "event/event/" + eventId + "/booking/request", {
         method: "post",
         headers: new Headers([['Content-Type', 'application/json']]),
         body: JSON.stringify(booking)
      });
      return (await result.text()) as EventBookingStatus.waitingList | EventBookingStatus.requested;
   }

   async addEventBooking(eventId: number, booking: EventBookingJson): Promise<EventBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "event/event/" + eventId + "/booking", {
         method: "post",
         body: JSON.stringify(booking),
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader])
      });

      return (await result.json()) as EventBookingJson;
   }

   async updateEventBooking(eventId: number, booking: EventBookingJson): Promise<EventBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "event/event/" + eventId + "/booking/" + booking.ID, {
         method: "put",
         body: JSON.stringify(booking),
         headers: new Headers([['Content-Type', 'application/json'], ...this.authHeader])
      });
      return (await result.json()) as EventBookingJson;
   }

   async deleteEventBooking(eventId: number, bookingId: number): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "event/event/" + eventId + "/booking/" + bookingId, {
         method: "delete",
         headers: new Headers(this.authHeader)
      });
   }

   async confirmEventBooking(eventId: number, bookingId: number): Promise<EventBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "event/event/" + eventId + "/booking/" + bookingId + "/confirm", {
         method: "put",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as EventBookingJson;
   }

   async cancelEventBooking(eventId: number, bookingId: number): Promise<EventBookingJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "event/event/" + eventId + "/booking/" + bookingId + "/cancel", {
         method: "put",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as EventBookingJson;
   }

   async getLocations(): Promise<LocationJson[]> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "location", {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as LocationJson[];
   }

   async getLocation(id: number): Promise<LocationJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "location/" + id, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as LocationJson;
   }

   async updateLocation(location: LocationJson): Promise<LocationJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "location/" + location.ID, {
         method: "put",
         headers: new Headers(this.authHeader.concat([['Content-Type', 'application/json']])),
         body: JSON.stringify(location),
      });

      return (await result.json()) as LocationJson;
   }

   async addLocation(location: LocationJson): Promise<LocationJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "location", {
         method: "post",
         headers: new Headers(this.authHeader.concat([['Content-Type', 'application/json']])),
         body: JSON.stringify(location)
      });

      return (await result.json()) as LocationJson;
   }

   async deleteLocation(id: number): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "location/" + id, {
         method: "delete",
         headers: new Headers(this.authHeader)
      });
   }

   async getMaterialItems(): Promise<MaterialItemJson[]> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "materialItem", {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as MaterialItemJson[];
   }

   async getMaterialItem(id: number): Promise<MaterialItemJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "materialItem/" + id, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as MaterialItemJson;
   }

   async updateMaterialItem(materialItem: MaterialItemJson): Promise<MaterialItemJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "materialItem/" + materialItem.ID, {
         method: "put",
         headers: new Headers(this.authHeader.concat([['Content-Type', 'application/json']])),
         body: JSON.stringify(materialItem),
      });

      return (await result.json()) as MaterialItemJson;
   }

   async addMaterialItem(materialItem: MaterialItemJson): Promise<MaterialItemJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "materialItem", {
         method: "post",
         headers: new Headers(this.authHeader.concat([['Content-Type', 'application/json']])),
         body: JSON.stringify(materialItem)
      });

      return (await result.json()) as MaterialItemJson;
   }

   async deleteMaterialItem(id: number): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "materialItem/" + id, {
         method: "delete",
         headers: new Headers(this.authHeader)
      });
   }

   async getPriceTemplates(): Promise<PriceTemplateJson[]> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "price/template", {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as PriceTemplateJson[];
   }

   async getPriceTemplate(id: number): Promise<PriceTemplateJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "price/template/" + id, {
         method: "get",
         headers: new Headers(this.authHeader)
      });

      return (await result.json()) as PriceTemplateJson;
   }

   async updatePriceTemplate(priceTemplate: PriceTemplateJson): Promise<PriceTemplateJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "price/template/" + priceTemplate.ID, {
         method: "put",
         headers: new Headers(this.authHeader.concat([['Content-Type', 'application/json']])),
         body: JSON.stringify(priceTemplate),
      });

      return (await result.json()) as PriceTemplateJson;
   }

   async addPriceTemplate(priceTemplate: PriceTemplateJson): Promise<PriceTemplateJson> {
      let result = await RestClient.request(RestClient.serverApiUrlBase + "price/template", {
         method: "post",
         headers: new Headers(this.authHeader.concat([['Content-Type', 'application/json']])),
         body: JSON.stringify(priceTemplate)
      });

      return (await result.json()) as PriceTemplateJson;
   }

   async deletePriceTemplate(id: number): Promise<void> {
      await RestClient.request(RestClient.serverApiUrlBase + "price/template/" + id, {
         method: "delete",
         headers: new Headers(this.authHeader)
      });
   }

}